import FVector from '@data/geometry/FVector';
import { BinaryReader } from 'Loader/serialization/common/BinaryReader';
import GeomSerializable, { GeomHeaderSchema } from './GeomSerializable';
import { GeometryChunkId } from './GeometryChunkId';

type BaseSchema = {
    version: number;
};
type SchemaV6 = BaseSchema & {
    EPSG: number;
    LandAnalysis: boolean;
    siteVolumeCenter: FVector;
    gridAngle: number;
    gridOrigin: FVector;
    siteLat: number;
    siteLon: number;
    siteAlt: number;
    K1: number;
    xyz: FVector[];
    K2: number;
    perimeters: FVector[];
    boundingBox: FVector[];
    gridX: number;
    gridY: number;
    gridZ: number;
    gridRes: number;
    N2: number;
    N1: number;
    Positions: {
        position: FVector;
        index: number;
    }[];
    NullPositions: {
        position: FVector;
        index: number;
    }[];
};
type SchemaV9 = BaseSchema & {
    EPSG: number;
    LandAnalysis: boolean;
    siteVolumeCenter: FVector;
    gridAngle: number;
    gridOrigin: FVector;
    siteLat: number;
    siteLon: number;
    siteAlt: number;
    K1: number;
    xyz: FVector[];
    K2: number;
    perimeters: FVector[];
    boundingBox: FVector[];
    gridX: number;
    gridY: number;
    gridZ: number;
    gridRes: number;
    N2: number;
    N1: number;
    Positions: {
        position: FVector;
        index: number;
    }[];
    NullPositions: {
        position: FVector;
        index: number;
    }[];
};
type SchemaV11 = BaseSchema & {
    EPSG: number;
    LandAnalysis: boolean;
    siteVolumeCenter: FVector;
    gridAngle: number;
    gridOrigin: FVector;
    siteLat: number;
    siteLon: number;
    siteAlt: number;
    K1: number;
    xyz: FVector[];
    K2: number;
    perimeters: FVector[];
    K3: number;
    buildables?: FVector[];
    boundingBox: FVector[];
    gridX: number;
    gridY: number;
    gridZ: number;
    gridRes: number;
    N2: number;
    N1: number;
    Positions: {
        position: FVector;
        index: number;
    }[];
    NullPositions: {
        position: FVector;
        index: number;
    }[];
};

type PreviousSchema = SchemaV6 | SchemaV9 | SchemaV11;
export type LatestGridSchema = SchemaV11;

class GridSerializer extends GeomSerializable<PreviousSchema, LatestGridSchema> {
    protected readonly expectedChunkId = GeometryChunkId.Grid;
    private static readonly LATEST_VERSION = 11;

    protected migrateToLatest(anyPrevious: PreviousSchema): LatestGridSchema {
        let migrated: PreviousSchema = anyPrevious;
        while (migrated.version < GridSerializer.LATEST_VERSION) {
            const migrate = this.getMigrationForVersion(migrated.version);
            if (!migrate) {
                throw new Error(`Missing Grid migration for version ${migrated.version}`);
            }
            migrated = migrate(migrated);
        }

        return migrated as LatestGridSchema;
    }

    protected deserializeContent(header: GeomHeaderSchema, reader: BinaryReader): LatestGridSchema {
        if (header.version >= 5 && header.version < 9) {
            return this.migrateToLatest(this.deserializeV6(header, reader));
        } else if (header.version >= 9 && header.version < 11) {
            return this.migrateToLatest(this.deserializeV9(header, reader));
        } else if (header.version >= 11) {
            return this.migrateToLatest(this.deserializeV11(header, reader));
        } else {
            throw new Error(`Unsupported Grid version: ${header.version}. Update your files`);
        }
    }

    private getMigrationForVersion(version: number): ((schema: PreviousSchema) => PreviousSchema) | undefined {
        if (version < 9) return (schema) => this.migrateV6ToV9(schema as SchemaV6);
        if (version < 11) return (schema) => this.migrateV9ToV11(schema as SchemaV9);
        return undefined;
    }

    private migrateV6ToV9(schema: SchemaV6): SchemaV9 {
        // No in-memory shape change from V6 to V9, but keep this explicit for stepwise upgrades.
        return { ...schema, version: 9 };
    }

    private migrateV9ToV11(schema: SchemaV9): SchemaV11 {
        return {
            ...schema,
            version: 11,
            K3: 0,
            buildables: [],
        };
    }

    private deserializeV6(header: GeomHeaderSchema, reader: BinaryReader): SchemaV6 {
        const EPSG = reader.readUInt32();
        const LandAnalysis = reader.readBoolean();
        const siteVolumeCenter = reader.readFVector();
        const gridAngle = reader.readFloat32();
        const gridOrigin = reader.readFVector();
        const siteLat = reader.readFloat64();
        const siteLon = reader.readFloat64();
        const siteAlt = reader.readFloat64();

        const K1 = reader.readUInt8();
        const xyz: FVector[] = [];
        for (let i = 0; i < K1; i++) {
            const point = reader.readFVector();
            xyz.push(point);
        }

        const K2 = reader.readUInt8();
        const perimeters: FVector[] = [];
        for (let i = 0; i < K2; i++) {
            const point = reader.readFVector();
            perimeters.push(point);
        }

        const boundingBox: FVector[] = [];
        for (let i = 0; i < 4; i++) {
            boundingBox.push(reader.readFVector());
        }

        const gridDims = reader.readFVector();
        const gridX = Math.round(gridDims.x);
        const gridY = Math.round(gridDims.y);
        const gridZ = Math.round(gridDims.z);

        const gridRes = reader.readFloat32();

        const N2 = reader.readUInt32();
        const N1 = reader.readUInt32();

        const Positions: SchemaV6['Positions'] = [];
        const NullPositions: SchemaV6['NullPositions'] = [];
        for (let n = 0; n < N1; n++) {
            const position = reader.readFVector();
            const index = reader.readUInt16();
            Positions.push({
                position,
                index,
            });
        }
        for (let n = 0; n < N2; n++) {
            const position = reader.readFVector();
            const index = reader.readUInt16();
            NullPositions.push({ position, index });
        }

        return {
            version: header.version,
            EPSG,
            LandAnalysis,
            siteVolumeCenter,
            gridAngle,
            gridOrigin,
            siteLat,
            siteLon,
            siteAlt,
            K1,
            xyz,
            K2,
            perimeters,
            boundingBox,
            gridX,
            gridY,
            gridZ,
            gridRes,
            N2,
            N1,
            Positions,
            NullPositions,
        };
    }

    private deserializeV9(header: GeomHeaderSchema, reader: BinaryReader): SchemaV9 {
        const EPSG = reader.readUInt32();
        const LandAnalysis = reader.readBoolean();
        const siteVolumeCenter = reader.readFVector();
        const gridAngle = reader.readFloat32();
        const gridOrigin = reader.readFVector();
        const siteLat = reader.readFloat64();
        const siteLon = reader.readFloat64();
        const siteAlt = reader.readFloat64();

        const K1 = reader.readUInt8();
        const xyz: FVector[] = [];
        for (let i = 0; i < K1; i++) {
            const point = reader.readFVector();
            xyz.push(point);
        }
        const K2 = reader.readUInt8();
        const perimeters: FVector[] = [];
        for (let i = 0; i < K2; i++) {
            const point = reader.readFVector();
            perimeters.push(point);
        }

        const boundingBox: FVector[] = [];
        for (let i = 0; i < 4; i++) {
            boundingBox.push(reader.readFVector());
        }

        const gridDims = reader.readFVector();
        const gridX = Math.round(gridDims.x);
        const gridY = Math.round(gridDims.y);
        const gridZ = Math.round(gridDims.z);

        const gridRes = reader.readFloat32();

        const N2 = reader.readUInt32();
        const N1 = reader.readUInt32();

        const Positions: SchemaV9['Positions'] = [];
        const NullPositions: SchemaV9['NullPositions'] = [];
        for (let n = 0; n < N1; n++) {
            const position = reader.readFVector();
            const index = reader.readUInt32();
            Positions.push({
                position,
                index,
            });
        }
        for (let n = 0; n < N2; n++) {
            const position = reader.readFVector();
            const index = reader.readUInt32();
            NullPositions.push({ position, index });
        }

        return {
            version: header.version,
            EPSG,
            LandAnalysis,
            siteVolumeCenter,
            gridAngle,
            gridOrigin,
            siteLat,
            siteLon,
            siteAlt,
            K1,
            xyz,
            K2,
            perimeters,
            boundingBox,
            gridX,
            gridY,
            gridZ,
            gridRes,
            N2,
            N1,
            Positions,
            NullPositions,
        };
    }

    private deserializeV11(header: GeomHeaderSchema, reader: BinaryReader): SchemaV11 {
        const EPSG = reader.readUInt32();
        const LandAnalysis = reader.readBoolean();
        const siteVolumeCenter = reader.readFVector();
        const gridAngle = reader.readFloat32();
        const gridOrigin = reader.readFVector();
        const siteLat = reader.readFloat64();
        const siteLon = reader.readFloat64();
        const siteAlt = reader.readFloat64();

        const K1 = reader.readUInt8();
        const xyz: FVector[] = [];
        for (let i = 0; i < K1; i++) {
            const point = reader.readFVector();
            xyz.push(point);
        }

        const K2 = reader.readUInt8();
        const perimeters: FVector[] = [];
        for (let i = 0; i < K2; i++) {
            const point = reader.readFVector();
            perimeters.push(point);
        }

        const K3 = reader.readUInt8();
        const buildables: FVector[] = [];
        for (let i = 0; i < K3; i++) {
            const point = reader.readFVector();
            buildables.push(point);
        }

        const boundingBox: FVector[] = [];
        for (let i = 0; i < 4; i++) {
            boundingBox.push(reader.readFVector());
        }

        const gridDims = reader.readFVector();
        const gridX = Math.round(gridDims.x);
        const gridY = Math.round(gridDims.y);
        const gridZ = Math.round(gridDims.z);

        const gridRes = reader.readFloat32();

        const N2 = reader.readUInt32();
        const N1 = reader.readUInt32();

        const Positions: SchemaV11['Positions'] = [];
        const NullPositions: SchemaV11['NullPositions'] = [];
        for (let n = 0; n < N1; n++) {
            const position = reader.readFVector();
            const index = reader.readUInt32();
            Positions.push({
                position,
                index,
            });
        }
        for (let n = 0; n < N2; n++) {
            const position = reader.readFVector();
            const index = reader.readUInt32();
            NullPositions.push({ position, index });
        }

        return {
            version: header.version,
            EPSG,
            LandAnalysis,
            siteVolumeCenter,
            gridAngle,
            gridOrigin,
            siteLat,
            siteLon,
            siteAlt,
            K1,
            xyz,
            K2,
            perimeters,
            K3,
            buildables,
            boundingBox,
            gridX,
            gridY,
            gridZ,
            gridRes,
            N2,
            N1,
            Positions,
            NullPositions,
        };
    }
}

export default GridSerializer;
